import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import xor from 'lodash/xor'
import { useEffect, useMemo } from 'react'
import { FieldErrors, useFieldArray } from 'react-hook-form'
import {
  IconButton,
  MaskedInputFieldProps,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  EventPickupTime,
  EventPickupTimeInput,
} from '@/clients/utoniq-core/schema'
import { tLabel, tMessage, useForm, UseFormProps, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import { MaskedInputCell, TextInputCell } from './common'

export type PickupTimeModalProps = {
  open: boolean
  onClose: () => void
  days: string[]
  onSubmit: (values: {
    pickupDate: string[]
    pickupTime: EventPickupTimeInput[]
  }) => void
}

type PickupTimeValue = {
  pickupDate: string[]
  pickupTime: EventPickupTimeInput[]
}

const schema: UseFormProps['schema'] = o => ({
  pickupDate: o
    .array()
    .items(o.string())
    .label(tLabel('consoleEventCollectionReceiveTimeEdit.pickupDate')),
  pickupTime: o.array().items(
    o.object({
      options: o.object({
        time: o
          .string()
          .regex(/^([01][0-9]|2[0-3]):([0-5][0-9])$/)
          .messages({
            'string.empty': tMessage(
              'consoleEventCollectionReceiveTimeEdit.pickupTime.time.string.empty'
            ),
          })
          .label(
            tLabel('consoleEventCollectionReceiveTimeEdit.pickupTime.time')
          ),
        availableUserNumber: o
          .number()
          .min(1)
          .label(
            tLabel(
              'consoleEventCollectionReceiveTimeEdit.pickupTime.availableUserNumber'
            )
          ),
      }),
    })
  ),
})

/**
 * @description
 * 一括で受け渡し日時を設定するモーダル
 * react-hook-formのスキーマを持ち、handleSubmit実行時に受取時間stateを更新するようになっている
 */
export const PickupTimeModal: Organism<PickupTimeModalProps> = ({
  open,
  days,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')

  const {
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    register,
    setValue,
    control,
    trigger,
    watch,
    reset,
  } = useForm<PickupTimeValue>({
    schema,
    defaultValues,
  })
  const watchPickupDate = watch('pickupDate')
  const watchPickupTime = watch('pickupTime')

  const {
    fields,
    append,
    remove: onRemove,
  } = useFieldArray({
    control,
    keyName: '_id',
    name: 'pickupTime',
  })

  const pickupDateError = useMemo(
    () => get(errors.pickupDate, 'message'),
    [errors.pickupDate]
  )

  useEffect(() => {
    if (!isSubmitSuccessful) return
    reset(defaultValues)
  }, [isSubmitSuccessful, reset])

  const handleOnSubmit = handleSubmit(values => {
    onSubmit(values)
    onClose()
  })

  const handleTriggerOrder = (
    errors?: FieldErrors<EventPickupTime['options']>
  ) => {
    if (!watchPickupTime.length) return
    if (errors) return
    setValue(
      'pickupTime',
      orderBy(
        cloneDeep(watchPickupTime),
        pickupTime => pickupTime?.options?.time || '24:00',
        ['asc']
      )
    )
    trigger('pickupTime')
  }

  return (
    <OrganismFrame>
      <Drawer
        open={open}
        onClose={onClose}
        sx={{
          // 単に props で指定するだけだと
          // default のスタイルが優先されるため
          // セレクタを用いた CSS 指定を行っている
          '& .MuiBackdrop-root': {
            // 画面幅に依らずバックドロップを画面全体にかける
            maxWidth: '100%',
          },
        }}
      >
        <Stack sx={{ p: 5 }} direction="row" alignItems="center">
          <Typography
            variant="h6"
            align="center"
            sx={{ width: '100%', ml: '30px' }}
          >
            {t('selectDateAndSetAll')}
          </Typography>
          <IconButton.Close edge="end" size="small" onClick={onClose} />
        </Stack>
        <Stack sx={{ px: 5, pb: 5 }}>
          <Typography variant="h6" sx={{ py: 2 }}>
            {t('eventDate')}
          </Typography>
          <Stack>
            <FormGroup row>
              {days.map(day => (
                <FormControlLabel
                  value={day}
                  control={<Checkbox sx={{ p: 2 }} />}
                  label={day}
                  onChange={() => {
                    setValue('pickupDate', xor(watchPickupDate, [day]), {
                      shouldValidate: true,
                    })
                  }}
                />
              ))}
            </FormGroup>
            {!!pickupDateError && (
              <FormHelperText error sx={{ bgcolor: 'transparent' }}>
                {pickupDateError}
              </FormHelperText>
            )}
          </Stack>
          <TableContainer sx={{ py: 4 }}>
            <Table>
              <TableHead
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <TableRow
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <TableCell
                    padding="normal"
                    sx={{ width: 'auto', pl: 0, pr: 2 }}
                  >
                    <Typography variant="subtitle1" whiteSpace="nowrap">
                      {t('deliveryStartTime', '受け渡し開始時間')}
                    </Typography>
                  </TableCell>
                  <TableCell
                    padding="normal"
                    sx={{
                      width: 100,
                      pl: 0,
                      pr: 2,
                      flex: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={4}
                      justifyContent="flex-end"
                    >
                      <Typography variant="subtitle1">
                        {t('availablePeople', '対応可能人数')}
                      </Typography>
                      <IconButton.Plus
                        bgcolor="primary.main"
                        sx={{
                          width: 20,
                          height: 20,
                          '&:hover': {
                            bgcolor: 'primary.main',
                            opacity: 0.8,
                          },
                          svg: {
                            fontSize: '12px',
                            color: 'white',
                          },
                        }}
                        size="small"
                        disabled={fields.length >= 20}
                        onClick={() => {
                          append({
                            options: {
                              time: '',
                              availableUserNumber: 0,
                            },
                          })
                        }}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => {
                  const { onChange: onChangeTime, ...timeInputProps } =
                    register(`pickupTime.${index}.options.time`)
                  const {
                    onChange: onChangeAvailableUserNumber,
                    ...availableUserNumberInputProps
                  } = register(
                    `pickupTime.${index}.options.availableUserNumber`
                  )
                  return (
                    <ModalPickupRowTime
                      key={field._id}
                      timeInputProps={{
                        ...timeInputProps,
                        async onChange(event) {
                          await onChangeTime(event)
                          await trigger(`pickupTime.${index}.options`)
                        },
                        async onBlur(event) {
                          await timeInputProps.onBlur(event)

                          handleTriggerOrder(
                            errors.pickupTime?.[index]?.options
                          )
                        },
                      }}
                      availableUserNumberInputProps={{
                        ...availableUserNumberInputProps,
                        async onChange(event) {
                          await onChangeAvailableUserNumber(event)
                          await trigger(`pickupTime.${index}.options`)
                        },
                        async onBlur(event) {
                          await availableUserNumberInputProps.onBlur(event)
                          handleTriggerOrder(
                            errors.pickupTime?.[index]?.options
                          )
                        },
                      }}
                      errors={errors.pickupTime?.[index]?.options}
                      onRemove={() => onRemove(index)}
                      bgcolor="transparent"
                    />
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack alignItems="center">
            <Button
              size="large"
              type="button"
              sx={{ width: 212 }}
              onClick={handleOnSubmit}
            >
              {t('register')}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </OrganismFrame>
  )
}

const defaultValues: PickupTimeValue = {
  pickupDate: [],
  pickupTime: [{ options: { time: '', availableUserNumber: 0 } }],
}

type PickupRowTimeProps = {
  timeInputProps: Omit<MaskedInputFieldProps, 't' | 'mask' | 'type'>
  availableUserNumberInputProps: Omit<TextInputFieldProps, 't' | 'type'>
  errors?: FieldErrors<EventPickupTime['options']>
  onRemove: () => void
  bgcolor?: string
  className?: string
}
export const ModalPickupRowTime: Organism<PickupRowTimeProps> = ({
  errors,
  timeInputProps,
  availableUserNumberInputProps,
  onRemove,
  bgcolor,
  className,
}) => {
  const errorMessage = useMemo(() => {
    if (errors?.time?.message) {
      return errors.time.message
    }
    if (errors?.availableUserNumber?.message) {
      return errors.availableUserNumber.message
    }
    return undefined
  }, [errors])

  return (
    <TableRow
      className={className}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        fieldset: {
          border: 'solid 1px lightgray',
        },
      }}
    >
      <TableCell
        padding="normal"
        sx={{
          borderBottom: 'none',
          pl: 0,
          pr: 4,
          pb: 0,
          position: 'relative',
          div: {
            xs: {
              minWidth: 'inherit',
            },
            '@media(min-width:450px)': {
              minWidth: 'auto',
              flex: 1,
            },
          },
          'p.Mui-error': {
            height: 0,
            position: 'absolute',
            bottom: 0,
            background: 'transparent',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Box>
          <MaskedInputCell
            {...timeInputProps}
            type="time"
            mask="00:00"
            textFieldProps={{
              error: !!errors?.time,
              helperText: errorMessage,
              FormHelperTextProps: { error: !!errors, sx: { bgcolor } },
            }}
          />
        </Box>
      </TableCell>
      <TableCell
        padding="normal"
        sx={{
          width: 'auto',
          borderBottom: 'none',
          pl: 0,
          pr: 0,
          pb: 0,
          div: {
            minWidth: 'inherit',
          },
          'p.Mui-error': {
            height: 0,
            position: 'absolute',
            bottom: 0,
            background: 'transparent',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box sx={{ '.MuiFormHelperText-root': { display: 'none' } }}>
            <TextInputCell
              type="number"
              titleProps={{ display: 'none' }}
              textFieldProps={{
                sx: {
                  my: 0,
                  width: 100,
                },
                error: !!errors?.availableUserNumber,
                helperText: errors ? ' ' : undefined,
                placeholder: '200',
                FormHelperTextProps: { sx: { bgcolor } },
              }}
              {...availableUserNumberInputProps}
            />
          </Box>
          <IconButton.Trash size="small" onClick={onRemove} />
        </Stack>
      </TableCell>
    </TableRow>
  )
}
